import { useEffect, useRef, useState } from "react";
import { showAlert, showPrompt } from "../lib/dialog";

interface Props {
  open: boolean;
  html: string;             // content with <mark class="lhk-hl"> highlights
  pageLabel: string;
  readOnly?: boolean;       // already published — show the content but grey out Publish
  elementor?: boolean;      // Elementor page — show the "content may look different" callout
  onClose: () => void;
  onPublish: (editedHtml: string) => Promise<void>;
}

// Review editor for Low-Hanging KW Insertion. Shows the rewritten page content in a
// contenteditable surface with the inserted keywords highlighted (mark.lhk-hl, styled
// in app.css). A WYSIWYG toolbar (same rt-toolbar/rt-editor system as the new-page
// creation cards) lets the user format the content after keywords have been inserted.
// The user reviews/edits, then Publishes to WordPress — LowHangingKw strips the
// highlight wrapper before writing the content back.
export function KeywordEditorDialog({ open, html, pageLabel, readOnly = false, elementor = false, onClose, onPublish }: Props) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [publishing, setPublishing] = useState(false);
  // Text-color picker: the native picker steals focus, so we capture the selection on
  // mousedown and restore it before applying foreColor. Declared here (with the other
  // hooks) — never after the early return below — so hook order stays stable.
  const colorRange = useRef<Range | null>(null);

  // Seed the editor imperatively when it opens so React never re-renders over edits.
  useEffect(() => {
    if (open && editorRef.current) editorRef.current.innerHTML = html;
  }, [open, html]);

  if (!open) return null;

  // Focus the editor, then run a document.execCommand against the current selection.
  const exec = (cmd: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
  };

  const onHeading = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sel = e.currentTarget;
    exec("formatBlock", sel.value === "P" ? "P" : sel.value); // e.g. "H2"
    sel.value = "P";
  };

  const onLink = async () => {
    // Capture the editor selection before the modal steals focus, then restore it so
    // execCommand("createLink") applies to the originally selected text.
    const selBefore = window.getSelection();
    const savedRange = selBefore && selBefore.rangeCount ? selBefore.getRangeAt(0).cloneRange() : null;
    const url = await showPrompt("Enter the URL to link to:", {
      title: "Add Link", tone: "info",
      placeholder: "https://example.com/page/", defaultValue: "https://",
      confirmLabel: "Add Link",
    });
    if (!url) return;
    editorRef.current?.focus();
    if (savedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedRange);
    }
    document.execCommand("createLink", false, url);
  };

  const onColorMouseDown = () => {
    const sel = window.getSelection();
    colorRange.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
  };
  const onColorInput = (e: React.FormEvent<HTMLInputElement>) => {
    editorRef.current?.focus();
    if (colorRange.current) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(colorRange.current);
    }
    // styleWithCSS → emits <span style="color:…"> (preserved by WordPress) rather than <font>.
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand("foreColor", false, (e.currentTarget as HTMLInputElement).value);
    document.execCommand("styleWithCSS", false, "false");
  };

  async function handlePublish() {
    if (publishing || !editorRef.current) return;
    setPublishing(true);
    try {
      await onPublish(editorRef.current.innerHTML);
    } catch (e) {
      void showAlert(e instanceof Error ? e.message : "Publish failed.", { title: "Publish Failed", tone: "danger" });
    } finally {
      setPublishing(false);
    }
  }

  const btn: React.CSSProperties = { padding: "9px 18px", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" };

  return (
    <div
      role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 4300, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "5vh 16px" }}
      onClick={publishing ? undefined : onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 12, maxWidth: 820, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.35)", padding: "22px 24px", color: "#334155", display: "flex", flexDirection: "column", maxHeight: "88vh" }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: 17, color: "#0f172a", fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px" }}>
          Review Keyword Insertion
        </h2>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, wordBreak: "break-all" }}>{pageLabel}</div>
        <p style={{ fontSize: 12.5, color: "#475569", margin: "0 0 12px", lineHeight: 1.5 }}>
          {readOnly ? (
            <>Inserted keywords are <mark className="lhk-hl">highlighted</mark>. This page has already been published to WordPress — it&rsquo;s shown here for reference and can&rsquo;t be published again.</>
          ) : (
            <>Inserted keywords are <mark className="lhk-hl">highlighted</mark>. Review, format, and edit as needed, then publish — the highlight is removed before the content is saved to WordPress.</>
          )}
        </p>

        {/* WYSIWYG toolbar — same rt-toolbar system as the new-page creation cards. */}
        <div className="rt-toolbar">
          <select className="rt-tb-heading" title="Heading level" defaultValue="P" onChange={onHeading}>
            <option value="P">Paragraph</option>
            <option value="H1">H1</option><option value="H2">H2</option><option value="H3">H3</option>
            <option value="H4">H4</option><option value="H5">H5</option><option value="H6">H6</option>
          </select>
          <button type="button" className="rt-tb-btn" title="Bold" onMouseDown={e => e.preventDefault()} onClick={() => exec("bold")}><strong>B</strong></button>
          <button type="button" className="rt-tb-btn" title="Add link" onMouseDown={e => e.preventDefault()} onClick={() => void onLink()}>🔗 Link</button>
          <button type="button" className="rt-tb-btn" title="Bulleted list" onMouseDown={e => e.preventDefault()} onClick={() => exec("insertUnorderedList")}>• Bullets</button>
          <label className="rt-tb-color-wrap" title="Text color">
            <input type="color" className="rt-tb-color" defaultValue="#1f2937" onMouseDown={onColorMouseDown} onInput={onColorInput} />
            <span>A</span>
          </label>
          <button type="button" className="rt-tb-btn" title="Underline" onMouseDown={e => e.preventDefault()} onClick={() => exec("underline")}><span style={{ textDecoration: "underline" }}>U</span></button>
          <button type="button" className="rt-tb-btn" title="Italic" onMouseDown={e => e.preventDefault()} onClick={() => exec("italic")}><em>I</em></button>
          <button type="button" className="rt-tb-btn" title="Align left" onMouseDown={e => e.preventDefault()} onClick={() => exec("justifyLeft")}><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="2" y1="4" x2="14" y2="4"/><line x1="2" y1="8" x2="10" y2="8"/><line x1="2" y1="12" x2="13" y2="12"/></svg></button>
          <button type="button" className="rt-tb-btn" title="Align center" onMouseDown={e => e.preventDefault()} onClick={() => exec("justifyCenter")}><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="3" y1="4" x2="13" y2="4"/><line x1="5" y1="8" x2="11" y2="8"/><line x1="4" y1="12" x2="12" y2="12"/></svg></button>
          <button type="button" className="rt-tb-btn" title="Align right" onMouseDown={e => e.preventDefault()} onClick={() => exec("justifyRight")}><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="2" y1="4" x2="14" y2="4"/><line x1="6" y1="8" x2="14" y2="8"/><line x1="3" y1="12" x2="14" y2="12"/></svg></button>
        </div>

        <div
          ref={editorRef}
          className="rt-editor"
          contentEditable
          suppressContentEditableWarning
          style={{ flex: 1, minHeight: 220 }}
        />

        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end", marginTop: 16 }}>
          {elementor && (
            <div style={{ marginRight: "auto", maxWidth: 400, display: "flex", gap: 8, alignItems: "flex-start", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "8px 11px" }}>
              <span aria-hidden style={{ color: "#2563eb", fontWeight: 800, fontSize: 13, lineHeight: 1.4, flexShrink: 0 }}>ⓘ</span>
              <span style={{ fontSize: 11, color: "#1e3a5f", lineHeight: 1.45 }}>
                This page was created with <strong>Elementor</strong>. The editor may show text from other widgets on the page, so it can look different from the live page. <strong>Only the highlighted changes</strong> will be applied to the existing page content when you Publish to WordPress.
              </span>
            </div>
          )}
          <button style={{ ...btn, background: "#e2e8f0", color: "#334155" }} onClick={onClose} disabled={publishing}>{readOnly ? "Close" : "Cancel"}</button>
          <button
            style={{ ...btn, background: readOnly ? "#94a3b8" : "#16a34a", color: "#fff", opacity: publishing ? 0.7 : 1, cursor: readOnly ? "not-allowed" : "pointer" }}
            onClick={() => void handlePublish()}
            disabled={publishing || readOnly}
            title={readOnly ? "Already published to WordPress" : undefined}
          >
            {publishing ? "Publishing…" : "Publish to WordPress"}
          </button>
        </div>
      </div>
    </div>
  );
}
